import { useCallback } from 'react';
import { AgentService } from '@/services/agent';
import { useAgentStore } from '@/stores/agentStore';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { AppNode, NodeStatus, NodeType } from '@/types';
import type { MediaGenerateResponse, GenerationParams } from '@/types/agent';
import { Message } from '../types';
import { useAgentMessages } from './useAgentMessages';
import { useAgentCanvasOps } from './useAgentCanvasOps';

export function useGenerationConfirm() {
  const { updateMessage } = useAgentMessages();
  const { syncCanvasGenerationNodeResult, ensureCanvasGenerationNode, ensureCanvasGridNode } = useAgentCanvasOps();

  const updateCanvasNode = useNodeStore((s) => s.updateNode);

  // 🟢 v4-#11: 统一的后端同步方法，确保 updateMessage 后 metadata 也同步到后端
  const syncMessageToBackend = useCallback(async (
    msgId: string | undefined,
    updates: {
      text?: string;
      type?: string;
      metadata?: Record<string, any>;
    },
  ) => {
    const currentSessionId = useAgentStore.getState().sessionId;
    if (!currentSessionId || !msgId) return;
    try {
      // 🔴 v7-#3: 使用 updateMessageMetadata 的 extra 参数合并为一次原子 API 调用
      if (updates.metadata) {
        await AgentService.updateMessageMetadata(msgId, currentSessionId, updates.metadata, {
          content: updates.text,
          msg_type: updates.type,
        });
      } else if (updates.text !== undefined && updates.type !== undefined) {
        await AgentService.updateMessageContent(msgId, currentSessionId, updates.text, updates.type);
      }
    } catch (err) {
      console.warn('[智能体] Failed to sync message to backend:', err);
    }
  }, []);

  const handleGenerationConfirm = useCallback(async (params: GenerationParams, confirmMsg: Message) => {
    if (useAgentStore.getState().isLoading) return;
    const store = useAgentStore.getState();
    store.setIsLoading(true);

    const { useProjectStore: _useProjectStore } = await import('@/stores/projectStore');
    const currentProjectId = _useProjectStore.getState().currentProjectId;
    if (currentProjectId) {
      await store.ensureSession(currentProjectId);
    }

    const genOptions = confirmMsg.generationOptions;
    const taskType = genOptions?.taskType || 'image';
    const isImageTask = taskType === 'image';
    const prompt = genOptions?.prompt || confirmMsg.pendingUserText || '';
    const currentModelId = useAgentStore.getState().currentModelId;
    const refNodeIds = confirmMsg.pendingRefNodeIds || [];
    const shouldUseImageInput = refNodeIds.length > 0 || (confirmMsg.pendingAttachments?.length ?? 0) > 0;

    const isGridFeature = genOptions?.feature;

    const confirmedText = isGridFeature
      ? `✅ 已确认「${isGridFeature}」生成参数：模型 ${params.model || '默认'}，尺寸 ${params.aspectRatio || '1:1'}${params.resolution ? `，分辨率 ${params.resolution}` : ''}`
      : `✅ 已确认生成参数：模型 ${params.model || '默认'}，尺寸 ${params.aspectRatio || '1:1'}${params.resolution ? `，分辨率 ${params.resolution}` : ''}${params.imageCount && params.imageCount > 1 ? `，${params.imageCount}张` : ''}`;

    updateMessage(confirmMsg.id, {
      type: 'text',
      text: confirmedText,
      generationOptions: undefined,
    });

    // 🟢 v4-#11: 使用统一的后端同步方法
    // 🔴 v5-#7: 确认时也清除 generation_options，避免恢复会话时残留
    await syncMessageToBackend(confirmMsg.id, {
      text: confirmedText,
      type: 'text',
      metadata: {
        is_generation_confirm: false,
        generation_confirmed: true,
        generation_options: null,
      },
    });

    const currentRefs = (confirmMsg.pendingRefNodeIds || []).map((id: string) => ({
      id,
      node: useNodeStore.getState().getNodeById(id) || ({ id, title: '', type: 'image', data: {} } as unknown as AppNode),
    }));

    let canvasNodeId: string;
    if (isGridFeature) {
      canvasNodeId = ensureCanvasGridNode({
        feature: isGridFeature,
        prompt,
        modelId: currentModelId,
        currentRefs,
        hasAttachmentInput: (confirmMsg.pendingAttachments?.length ?? 0) > 0,
        generationParams: params,
      });
    } else {
      canvasNodeId = ensureCanvasGenerationNode({
        taskType: isImageTask ? 'image' : 'video',
        prompt,
        modelId: currentModelId,
        currentRefs,
        hasAttachmentInput: (confirmMsg.pendingAttachments?.length ?? 0) > 0,
        generationParams: params,
      });
    }

    try {
      const executeResult = await AgentService.execute({
        project_id: useAgentStore.getState().projectId || '',
        message: prompt,
        model_id: currentModelId,
        session_id: useAgentStore.getState().sessionId || undefined,
        confirm_msg_id: confirmMsg.id || undefined,
        attachments: confirmMsg.pendingAttachments || [],
        references: confirmMsg.pendingReferences || [],
        generation_params: isGridFeature ? { ...params, feature: isGridFeature } : params,
        agent_id: useAgentStore.getState().currentAgentId || undefined,
      });

      if (executeResult.agentId) {
        useAgentStore.getState().setCurrentAgentId(executeResult.agentId);
      }

      if (executeResult.type === 'confirm' && executeResult.generationOptions) {
        useAgentStore.getState().setIsLoading(false);
        updateMessage(confirmMsg.id, {
          type: 'generation_confirm',
          text: executeResult.content || '请确认生成参数',
          generationOptions: executeResult.generationOptions,
        });
      } else if (executeResult.type === 'task' && executeResult.taskId) {
        const nodeType = isImageTask ? NodeType.IMAGE_GENERATOR : NodeType.VIDEO_GENERATOR;
        useTaskStore.getState().addTaskWithRequest(executeResult.taskId, canvasNodeId, nodeType, {
          modelId: params.model || currentModelId,
          prompt,
          params: {
            aspectRatio: params.aspectRatio || '16:9',
            resolution: params.resolution,
            imageCount: params.imageCount,
            generationMode: shouldUseImageInput ? 'IMG2IMG' : 'TXT2IMG',
          },
        });
        updateCanvasNode(canvasNodeId, { taskId: executeResult.taskId, progress: '任务已提交，等待生成...' });
        const pendingMediaData: MediaGenerateResponse = {
          taskType: isImageTask ? 'image' : 'video',
          taskId: executeResult.taskId,
          status: 'RUNNING',
          prompt,
          modelId: params.model || currentModelId,
          aspectRatio: params.aspectRatio,
          results: [],
          projectId: useAgentStore.getState().projectId || '',
        };
        updateMessage(confirmMsg.id, {
          type: isImageTask ? 'image' : 'video',
          text: '',
          generationOptions: undefined,
          mediaData: pendingMediaData,
          metadata: {
            ...(confirmMsg.metadata || {}),
            pendingNodeId: canvasNodeId,
            pendingTaskId: executeResult.taskId,
            taskStatus: 'RUNNING',
          },
        });
      } else if ((executeResult.intent?.name === 'generate_image' && executeResult.messageType === 'image') ||
          (executeResult.intent?.name === 'generate_video' && executeResult.messageType === 'video') ||
          (executeResult.intent?.name === 'grid_generation' && executeResult.messageType === 'image')) {
        const mediaData = JSON.parse(executeResult.content || '{}') as MediaGenerateResponse;
        syncCanvasGenerationNodeResult(canvasNodeId, mediaData);

        const isPending = mediaData.status === 'PENDING' || mediaData.status === 'RUNNING' || mediaData.status === 'QUEUED';
        if (isPending && mediaData.taskId) {
          const nodeType = mediaData.taskType === 'image' ? NodeType.IMAGE_GENERATOR : NodeType.VIDEO_GENERATOR;
          useTaskStore.getState().addTaskWithRequest(mediaData.taskId, canvasNodeId, nodeType, {
            modelId: mediaData.modelId || params.model,
            prompt: mediaData.prompt || prompt,
            params: {
              aspectRatio: params.aspectRatio || '16:9',
              resolution: params.resolution,
              imageCount: params.imageCount,
              generationMode: shouldUseImageInput ? 'IMG2IMG' : 'TXT2IMG',
            },
          });
          updateCanvasNode(canvasNodeId, { taskId: mediaData.taskId });
        }

        const resultText = mediaData.results?.length > 0
          ? (mediaData.taskType === 'image'
            ? `🖼️ 图片生成完成，共 ${mediaData.results.length} 张`
            : `🎬 视频生成完成`)
          : (mediaData.taskType === 'image' ? '图片生成任务已提交，等待结果...' : '视频生成任务已提交，等待结果...');

        updateMessage(confirmMsg.id, {
          type: mediaData.results?.length > 0 ? mediaData.taskType : 'text',
          text: mediaData.results?.length > 0 ? '' : resultText,
          generationOptions: undefined,
          mediaData: mediaData.results?.length > 0 ? mediaData : undefined,
          ...(isPending && mediaData.taskId ? {
            metadata: {
              ...(confirmMsg.metadata || {}),
              pendingNodeId: canvasNodeId,
              pendingTaskId: mediaData.taskId,
              taskStatus: 'RUNNING',
            },
          } : {}),
        });
      } else if (executeResult.type === 'stream' && (executeResult as any).streamId) {
        // 🔴 v8-#4: 流式结果 — 更新消息为等待状态，由 SSE 推送后续内容
        updateMessage(confirmMsg.id, {
          type: 'text',
          text: '生成任务已提交，正在处理中...',
          generationOptions: undefined,
        });
        await syncMessageToBackend(confirmMsg.id, {
          text: '生成任务已提交，正在处理中...',
          type: 'text',
          metadata: {
            is_generation_confirm: false,
            generation_confirmed: true,
            generation_options: null,
          },
        });
      } else {
        updateCanvasNode(canvasNodeId, {
          progress: undefined,
          error: '生成失败：未识别到生成意图',
        }, undefined, undefined, NodeStatus.ERROR);
        updateMessage(confirmMsg.id, {
          type: 'error',
          text: '生成失败：未识别到生成意图',
          generationOptions: undefined,
        });
      }
    } catch (err: any) {
      updateCanvasNode(canvasNodeId, {
        progress: undefined,
        error: `生成失败：${err.message || '未知错误'}`,
      }, undefined, undefined, NodeStatus.ERROR);
      updateMessage(confirmMsg.id, {
        type: 'error',
        text: `生成失败：${err.message || '未知错误'}`,
        generationOptions: undefined,
      });
    } finally {
      useAgentStore.getState().setIsLoading(false);
    }
  }, [updateMessage, updateCanvasNode, syncCanvasGenerationNodeResult, ensureCanvasGenerationNode, ensureCanvasGridNode, syncMessageToBackend]);

  const handleGenerationCancel = useCallback(async (confirmMsg: Message) => {
    const cancelledText = '❌ 已取消生成';
    updateMessage(confirmMsg.id, {
      type: 'text',
      text: cancelledText,
      generationOptions: undefined,
    });

    // 🟢 v4-#11: 使用统一的后端同步方法
    await syncMessageToBackend(confirmMsg.id, {
      text: cancelledText,
      type: 'text',
      metadata: {
        is_generation_confirm: false,
        generation_confirmed: false,
        cancelled: true,
        generation_options: null,
      },
    });
  }, [updateMessage, syncMessageToBackend]);

  return { handleGenerationConfirm, handleGenerationCancel };
}
